import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BasicFilters } from '../ui/basic-filters'
import { HorizontalCarousel } from '../ui/horizontal-carousel'
import BookCard from '../BookCard'
import { LibraryBook, BOOK_GENRES } from '../../types'

// Skeleton card component matching BookCard dimensions
function SkeletonBookCard() {
  return (
    <div className="skeleton-book-card animate-pulse">
      {/* Cover skeleton - matches aspect-[2/3] */}
      <div className="aspect-[2/3] mb-3 bg-muted rounded-sm" />
      {/* Title skeleton */}
      <div className="h-4 bg-muted rounded w-full mb-2" />
      <div className="h-4 bg-muted rounded w-3/4 mb-2" />
      {/* Author skeleton */}
      <div className="h-3 bg-muted rounded w-1/2" />
    </div>
  )
}

interface BookCarouselSectionProps {
  title: string
  books: LibraryBook[]
  filters?: string[]
  showLanguageFilters?: boolean
  showSeeAll?: boolean
  seeAllRoute?: string
  className?: string
  languageFilter?: string | string[] // Can be a single language name or array of language codes
  isLoading?: boolean
}

export function BookCarouselSection({ 
  title, 
  books, 
  filters = [], 
  showLanguageFilters = false,
  showSeeAll = true,
  seeAllRoute = '/library',
  className = '',
  languageFilter = '',
  isLoading = false
}: BookCarouselSectionProps) {
  const [activeFilter, setActiveFilter] = useState<string>('')
  const navigate = useNavigate()

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter === activeFilter ? '' : filter)
  }

  const getSeeAllHref = () => {
    // Check if the title is a genre
    const isGenre = BOOK_GENRES.includes(title as any)
    
    // Build href with genre parameter if it's a genre section
    if (isGenre) {
      const params = new URLSearchParams()
      params.set('genre', title)
      if (languageFilter) {
        const langValue = Array.isArray(languageFilter) ? languageFilter.join(',') : languageFilter
        params.set('language', langValue)
      }
      return `${seeAllRoute}?${params.toString()}`
    } else {
      if (languageFilter) {
        const langValue = Array.isArray(languageFilter) ? languageFilter.join(',') : languageFilter
        return `${seeAllRoute}?language=${encodeURIComponent(langValue)}`
      } else {
        return seeAllRoute
      }
    }
  }

  const handleSeeAll = () => {
    navigate(getSeeAllHref())
  }

  // Use external languageFilter if provided, otherwise use internal activeFilter
  const effectiveFilter = languageFilter || activeFilter
  
  // Handle both single string filter and array of language codes
  const filteredBooks = effectiveFilter 
    ? Array.isArray(effectiveFilter)
      ? books.filter(book => {
          // For array: check if book has any of the selected languages
          if (!book.available_languages || effectiveFilter.length === 0) return true
          
          // available_languages contains language codes (e.g., "en", "nb", "es")
          // effectiveFilter also contains language codes
          // So compare codes directly
          const hasMatch = effectiveFilter.some(code => book.available_languages?.includes(code))
          
          return hasMatch
        })
      : books.filter(book => book.available_languages?.includes(effectiveFilter))
    : books

  return (
    <section className={`py-2 ${className}`}>
      <BasicFilters
        title={title}
        filters={showLanguageFilters ? filters : []}
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        showSeeAll={showSeeAll}
        seeAllHref={getSeeAllHref()}
        onSeeAll={handleSeeAll}
      />
      
      {isLoading ? (
        <HorizontalCarousel>
          {[...Array(6)].map((_, index) => (
            <div key={`skeleton-${index}`} className="flex-shrink-0 w-48">
              <SkeletonBookCard />
            </div>
          ))}
        </HorizontalCarousel>
      ) : filteredBooks.length > 0 ? (
        <HorizontalCarousel>
          {filteredBooks.map((book) => (
            <div key={book.id} className="flex-shrink-0 w-48">
              <BookCard 
                book={book} 
                variant="library-book"
              />
            </div>
          ))}
        </HorizontalCarousel>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <p>No books found for the selected filter.</p>
        </div>
      )}
    </section>
  )
}
